import React, { useState, useEffect, useMemo } from "react";
import { modalBoxStyle } from "../styles";
import {
  Typography,
  Box,
  Modal,
  Grid,
  IconButton,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { debounce } from "lodash";
import inventoryClient from "../../../clients/inventoryClient";
import ProductSelectionStep from "./components/ProductSelectionStep";
import AreaSelectionStep from "./components/AreaSelectionStep";
import QuantityEntryStep from "./components/QuantityEntryStep";
import { useMutation } from "react-query";

const STEP_PRODUCT = 1;
const STEP_AREAS = 2;
const STEP_QUANTITIES = 3;

const AddProductToCompletedStockCountModal = ({
  isOpen,
  stockCountId,
  establishmentId,
  areaCounts = [],
  prefilledAreaCountId = null,
  onSuccessCallback,
  handleCloseAddProductModal,
}) => {
  const [step, setStep] = useState(STEP_PRODUCT);
  const [inputValue, setInputValue] = useState("");
  const [searchString, setSearchString] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [existingAreaIds, setExistingAreaIds] = useState(new Set());
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [membershipError, setMembershipError] = useState(null);
  const [selectedAreaIds, setSelectedAreaIds] = useState([]);
  const [quantityByAreaId, setQuantityByAreaId] = useState({});

  const prefilledArea = useMemo(
    () =>
      prefilledAreaCountId
        ? areaCounts.find((a) => a.id === prefilledAreaCountId) || null
        : null,
    [prefilledAreaCountId, areaCounts]
  );

  const resetState = () => {
    setStep(STEP_PRODUCT);
    setInputValue("");
    setSearchString("");
    setSearchResults([]);
    setSearchLoading(false);
    setSelectedProduct(null);
    setExistingAreaIds(new Set());
    setMembershipLoading(false);
    setMembershipError(null);
    setSelectedAreaIds([]);
    setQuantityByAreaId({});
  };

  useEffect(() => {
    if (isOpen) {
      resetState();
    }
  }, [isOpen]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSearchString(value);
      }, 300),
    []
  );

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  useEffect(() => {
    let cancelled = false;
    if (!searchString) {
      setSearchLoading(false);
      setSearchResults([]);
      return () => {
        cancelled = true;
      };
    }
    setSearchLoading(true);
    inventoryClient
      .searchProductFormatsSlim(establishmentId, searchString)
      .then((res) => {
        if (cancelled) return;
        setSearchResults(res?.results || []);
      })
      .catch(() => {
        if (cancelled) return;
        setSearchResults([]);
      })
      .finally(() => {
        if (cancelled) return;
        setSearchLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [searchString, establishmentId]);

  // Fetch which areas this product already has records in.
  useEffect(() => {
    if (!selectedProduct) {
      setExistingAreaIds(new Set());
      setMembershipError(null);
      setMembershipLoading(false);
      return;
    }
    let cancelled = false;
    setMembershipLoading(true);
    setMembershipError(null);
    inventoryClient
      .getCompletedCountFormatAreaMembership({
        establishmentId,
        stockCountId,
        productFormatIds: [selectedProduct.productFormatId],
      })
      .then((res) => {
        if (cancelled) return;
        const match = (res?.memberships || []).find(
          (m) => m.productFormatId === selectedProduct.productFormatId
        );
        setExistingAreaIds(new Set(match?.areaCountIds || []));
      })
      .catch((e) => {
        if (cancelled) return;
        setMembershipError(
          e?.response?.data?.message ||
            e?.message ||
            "Could not check existing areas for this product."
        );
        setExistingAreaIds(new Set());
      })
      .finally(() => {
        if (cancelled) return;
        setMembershipLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedProduct, establishmentId, stockCountId]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedSearch(value);
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
  };

  const handleClearProduct = () => {
    setSelectedProduct(null);
    setInputValue("");
    setSearchString("");
    setSearchResults([]);
  };

  const handleToggleArea = (areaId) => {
    setSelectedAreaIds((prev) =>
      prev.includes(areaId)
        ? prev.filter((x) => x !== areaId)
        : [...prev, areaId]
    );
  };

  const handleQuantityChange = (areaCountId, quantity) => {
    setQuantityByAreaId((prev) => ({ ...prev, [areaCountId]: quantity }));
  };

  // Step 1 blocking message — shown when the chosen product can't proceed.
  const blockingMessage = useMemo(() => {
    if (!selectedProduct || membershipLoading) return null;
    if (prefilledAreaCountId && existingAreaIds.has(prefilledAreaCountId)) {
      return `${
        selectedProduct.productName || selectedProduct.displayName
      } is already counted in ${
        prefilledArea?.name || "this area"
      }. Pick a different product.`;
    }
    if (
      !prefilledAreaCountId &&
      areaCounts.length > 0 &&
      existingAreaIds.size >= areaCounts.length
    ) {
      return `${
        selectedProduct.productName || selectedProduct.displayName
      } is already counted in every area on this count.`;
    }
    return null;
  }, [
    selectedProduct,
    membershipLoading,
    existingAreaIds,
    prefilledAreaCountId,
    prefilledArea,
    areaCounts,
  ]);

  const handleNextFromProduct = () => {
    if (!selectedProduct || membershipLoading || blockingMessage) return;
    if (prefilledAreaCountId) {
      setSelectedAreaIds([prefilledAreaCountId]);
      setStep(STEP_QUANTITIES);
    } else {
      setStep(STEP_AREAS);
    }
  };

  const handleNextFromAreas = () => {
    setStep(STEP_QUANTITIES);
  };

  const handleBack = () => {
    if (step === STEP_QUANTITIES) {
      if (prefilledAreaCountId) {
        setSelectedAreaIds([]);
        setQuantityByAreaId({});
        setStep(STEP_PRODUCT);
      } else {
        setQuantityByAreaId({});
        setStep(STEP_AREAS);
      }
    } else if (step === STEP_AREAS) {
      setSelectedAreaIds([]);
      setStep(STEP_PRODUCT);
    }
  };

  const addRecordsMutation = useMutation(
    inventoryClient.addRecordsToCompletedStockCount,
    {
      onSuccess: () => {
        onSuccessCallback();
        closeModal();
      },
      onError: (error) => {
        console.error("Error adding records to completed stock count:", error);
      },
    }
  );

  const handleSave = () => {
    const records = selectedAreaIds.map((areaCountId) => ({
      productFormatId: selectedProduct.productFormatId,
      areaCountId,
      quantityFound: parseFloat(quantityByAreaId[areaCountId]),
    }));
    addRecordsMutation.mutate({ establishmentId, stockCountId, records });
  };

  const closeModal = () => {
    handleCloseAddProductModal();
  };

  const allQuantitiesEntered = selectedAreaIds.every((areaId) => {
    const v = quantityByAreaId[areaId];
    return v !== undefined && v !== "" && parseFloat(v) >= 0;
  });

  const totalSteps = prefilledAreaCountId ? 2 : 3;
  const displayStep =
    step === STEP_QUANTITIES && prefilledAreaCountId ? 2 : step;

  const stepTitle = (() => {
    if (step === STEP_PRODUCT) return "Add From Library";
    if (step === STEP_AREAS) return "Specify Areas";
    return "Input Count Quantity";
  })();

  const stepSubtitle = prefilledArea
    ? `Adding to ${prefilledArea.name}`
    : `Step ${displayStep} of ${totalSteps}`;

  return (
    <Modal
      open={isOpen}
      onClose={closeModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={modalBoxStyle}>
        {addRecordsMutation.isLoading ? (
          <Grid
            container
            direction="row"
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
            }}
          >
            <CircularProgress />
          </Grid>
        ) : (
          <>
            <Box sx={{ marginBottom: 4 }}>
              <Grid
                container
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: step === STEP_PRODUCT ? 0 : 2,
                }}
              >
                <Grid item>
                  {step !== STEP_PRODUCT ? (
                    <Button
                      startIcon={<ChevronLeftIcon />}
                      onClick={handleBack}
                      sx={{
                        color: (theme) => theme.palette.primary.main,
                        textTransform: "none",
                        fontSize: "16px",
                        padding: 0,
                        minWidth: "auto",
                        "&:hover": { backgroundColor: "transparent" },
                      }}
                    >
                      Back
                    </Button>
                  ) : null}
                </Grid>
                <Grid item>
                  <IconButton
                    sx={{ backgroundColor: "transparent" }}
                    onClick={closeModal}
                  >
                    <CloseOutlinedIcon />
                  </IconButton>
                </Grid>
              </Grid>
              <Box>
                <Typography variant="h4">{stepTitle}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {stepSubtitle}
                </Typography>
              </Box>
            </Box>

            {addRecordsMutation.isError && (
              <Box sx={{ marginTop: 3, marginBottom: 2 }}>
                <Alert severity="error">
                  {addRecordsMutation.error?.response?.data?.message ||
                    addRecordsMutation.error?.message ||
                    "Failed to add records to stock count. Please try again."}
                </Alert>
              </Box>
            )}

            {step === STEP_PRODUCT && (
              <ProductSelectionStep
                inputValue={inputValue}
                onInputChange={handleInputChange}
                searchResults={searchResults}
                loading={searchLoading}
                selectedProduct={selectedProduct}
                onSelectProduct={handleSelectProduct}
                onClearProduct={handleClearProduct}
                onNext={handleNextFromProduct}
                membershipLoading={membershipLoading}
                membershipError={membershipError}
                blockingMessage={blockingMessage}
                nextLabel={prefilledAreaCountId ? "Enter Quantity" : "Next"}
              />
            )}

            {step === STEP_AREAS && (
              <AreaSelectionStep
                areaCounts={areaCounts}
                existingAreaIds={existingAreaIds}
                selectedAreaIds={selectedAreaIds}
                onToggleArea={handleToggleArea}
                onNext={handleNextFromAreas}
              />
            )}

            {step === STEP_QUANTITIES && (
              <QuantityEntryStep
                selectedProduct={selectedProduct}
                selectedAreaIds={selectedAreaIds}
                areaCounts={areaCounts}
                quantityByAreaId={quantityByAreaId}
                onQuantityChange={handleQuantityChange}
                onSave={handleSave}
                canSave={allQuantitiesEntered}
                saveLabel={
                  prefilledAreaCountId
                    ? "Add Product To Area"
                    : "Add Product To Count"
                }
              />
            )}
          </>
        )}
      </Box>
    </Modal>
  );
};

export default AddProductToCompletedStockCountModal;
